/**
 * ТЗ 0092 — лог участия дофинансированных: чистые помощники.
 *
 * Все коды с сервера — строки в нижнем регистре; подпись и тон даёт фронт.
 * Ключи i18n записаны ЦЕЛИКОМ, а не собраны шаблонной строкой: так их видит
 * поиск по коду и тест локалей (`participation.locale.test.ts`), и
 * пропущенный перевод ловится тестом, а не глазами на экране.
 */

/** Совпадает по значениям с `TagTone` из `_detailShared/detail.styled`. */
export type ParticipationTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'neutral'
  | 'info'
  | 'gold';

export type ParticipationLanguage = 'pl' | 'ru';

/** Язык файла: операторам BUR/KFS отдаётся польский, русский — по запросу. */
export const PARTICIPATION_LANGUAGES: ParticipationLanguage[] = ['pl', 'ru'];
export const DEFAULT_PARTICIPATION_LANGUAGE: ParticipationLanguage = 'pl';

export const PARTICIPATION_LANGUAGE_KEYS: Record<
  ParticipationLanguage,
  string
> = {
  pl: 'pages.dofinansowanie.participation.language.pl',
  ru: 'pages.dofinansowanie.participation.language.ru',
};

/** Предупреждения сводки (`ParticipationWarningType.code`). */
export const PARTICIPATION_WARNING_KEYS: Record<string, string> = {
  not_fetched: 'pages.dofinansowanie.participation.warnings.not_fetched',
  no_zoom_data: 'pages.dofinansowanie.participation.warnings.no_zoom_data',
  expired: 'pages.dofinansowanie.participation.warnings.expired',
  offline: 'pages.dofinansowanie.participation.warnings.offline',
  lesson_ambiguous:
    'pages.dofinansowanie.participation.warnings.lesson_ambiguous',
  zoom_forbidden: 'pages.dofinansowanie.participation.warnings.zoom_forbidden',
  unresolved_participants:
    'pages.dofinansowanie.participation.warnings.unresolved_participants',
  journal_mismatch:
    'pages.dofinansowanie.participation.warnings.journal_mismatch',
  terminated: 'pages.dofinansowanie.participation.warnings.terminated',
  no_lessons: 'pages.dofinansowanie.participation.warnings.no_lessons',
  lesson_time_invalid:
    'pages.dofinansowanie.participation.warnings.lesson_time_invalid',
  no_zoom_source: 'pages.dofinansowanie.participation.warnings.no_zoom_source',
};
export const PARTICIPATION_WARNING_UNKNOWN_KEY =
  'pages.dofinansowanie.participation.warnings.unknown';

export const participationWarningKey = (code?: string | null): string =>
  (code && PARTICIPATION_WARNING_KEYS[code]) ||
  PARTICIPATION_WARNING_UNKNOWN_KEY;

export const PARTICIPATION_WARNING_LESSONS_KEY =
  'pages.dofinansowanie.participation.warnings.lessons';
export const PARTICIPATION_WARNING_SESSIONS_KEY =
  'pages.dofinansowanie.participation.warnings.sessions';

/**
 * В чём считается `count` предупреждения — единица пишется явно.
 *
 * `unresolved_participants`: число — ЗАХОДЫ Zoom, а даты — занятия, и «2»
 * рядом с одной датой читалось бы как «два занятия». У `terminated` и
 * `no_lessons` числа нет вовсе (договор один, занятий ноль). Всё прочее —
 * занятия.
 */
export const participationWarningCountKey = (
  code?: string | null,
): string | null => {
  if (code === 'terminated' || code === 'no_lessons') return null;
  if (code === 'unresolved_participants') {
    return PARTICIPATION_WARNING_SESSIONS_KEY;
  }
  return PARTICIPATION_WARNING_LESSONS_KEY;
};

/** Статус занятия в отчёте (`ParticipationLessonType.status`). */
export const PARTICIPATION_LESSON_STATUS_KEYS: Record<string, string> = {
  attended: 'pages.dofinansowanie.participation.lessonStatus.attended',
  partial: 'pages.dofinansowanie.participation.lessonStatus.partial',
  absent: 'pages.dofinansowanie.participation.lessonStatus.absent',
  no_zoom_data: 'pages.dofinansowanie.participation.lessonStatus.no_zoom_data',
  expired: 'pages.dofinansowanie.participation.lessonStatus.expired',
  not_fetched: 'pages.dofinansowanie.participation.lessonStatus.not_fetched',
  future: 'pages.dofinansowanie.participation.lessonStatus.future',
  offline: 'pages.dofinansowanie.participation.lessonStatus.offline',
};
export const PARTICIPATION_LESSON_STATUS_UNKNOWN_KEY =
  'pages.dofinansowanie.participation.lessonStatus.unknown';

export const participationLessonStatusKey = (code?: string | null): string =>
  (code && PARTICIPATION_LESSON_STATUS_KEYS[code]) ||
  PARTICIPATION_LESSON_STATUS_UNKNOWN_KEY;

const LESSON_STATUS_TONES: Record<string, ParticipationTone> = {
  attended: 'ok',
  partial: 'warn',
  absent: 'danger',
  no_zoom_data: 'warn',
  expired: 'warn',
  not_fetched: 'info',
  future: 'neutral',
  offline: 'neutral',
};

export const participationLessonStatusTone = (
  code?: string | null,
): ParticipationTone => (code && LESSON_STATUS_TONES[code]) || 'neutral';

/** Отметка журнала (`ParticipationLessonType.journal`). */
export const PARTICIPATION_JOURNAL_KEYS: Record<string, string> = {
  present: 'pages.dofinansowanie.participation.journal.present',
  absent: 'pages.dofinansowanie.participation.journal.absent',
  unmarked: 'pages.dofinansowanie.participation.journal.unmarked',
  none: 'pages.dofinansowanie.participation.journal.none',
};

export const participationJournalKey = (code?: string | null): string =>
  (code && PARTICIPATION_JOURNAL_KEYS[code]) || PARTICIPATION_JOURNAL_KEYS.none;

const JOURNAL_TONES: Record<string, ParticipationTone> = {
  present: 'ok',
  absent: 'danger',
  unmarked: 'neutral',
  none: 'neutral',
};

export const participationJournalTone = (
  code?: string | null,
): ParticipationTone => (code && JOURNAL_TONES[code]) || 'neutral';

/** Ключи ошибок скачивания и периода. */
export const PARTICIPATION_FILE_UNAVAILABLE_KEY =
  'pages.dofinansowanie.participation.download.unavailable';
export const PARTICIPATION_PERIOD_INVALID_KEY =
  'pages.dofinansowanie.participation.period.invalid';

/**
 * Период ок? `YYYY-MM-DD` сравниваются строкой. Пустой край — «от начала /
 * до конца окна договора», это не ошибка.
 */
export const participationPeriodError = (
  dateFrom?: string | null,
  dateTo?: string | null,
): string | null =>
  dateFrom && dateTo && dateFrom > dateTo
    ? PARTICIPATION_PERIOD_INVALID_KEY
    : null;

export interface IParticipationAgreementLike {
  id: string;
  isCandidateAgreement: boolean;
  cofunded: boolean;
}

/**
 * Договор по умолчанию в карточке кандидата: договор самого кандидата, а если
 * его нет — единственный. Из нескольких чужих «какой-нибудь» не выбираем:
 * сводка по чужому договору читалась бы как сводка по этому человеку.
 */
export const defaultParticipationAgreementId = (
  agreements: readonly IParticipationAgreementLike[],
): string | null => {
  const own = agreements.find((agreement) => agreement.isCandidateAgreement);
  if (own) return own.id;
  return agreements.length === 1 ? agreements[0].id : null;
};

/**
 * Процент одним знаком после запятой, по-русски и по-польски — с запятой:
 * `100`, `71,4`. `null` — посчитать не из чего (нет проведённых занятий).
 */
export const formatParticipationPercent = (
  value: number | null | undefined,
  language = 'ru',
): string | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const locale = language.startsWith('pl') ? 'pl-PL' : 'ru-RU';
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
  }).format(value)}%`;
};

/** `2026-09-21` → `21.09.2026`; незнакомое — как есть. */
export const formatIsoDate = (iso?: string | null): string => {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : iso;
};

/** Сколько дат предупреждения показывать до «+N». */
export const WARNING_DATES_SHOWN = 6;
